import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from "react-router-dom";
import DataTable from 'react-data-table-component';
import moment from 'moment';
import ReportsV2 from '../Services/ReportsV2';
import Alert from '../Services/Alert';
import { getDateFormat } from '../Services/Custom';

const CURRENCY = process.env.REACT_APP_CURRENCY_SYMBOL || '₹';
const money = (v) => `${CURRENCY}${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WIN_OPTIONS = [
    { value: '', label: 'All' },
    { value: '0', label: 'Not settled' },
    { value: '1', label: 'Won' },
    { value: '2', label: 'Lost' },
    { value: '3', label: 'Refunded' },
    { value: '4', label: 'Rolled back' },
];

/**
 * One page for all eight bid category reports, selected by the :type route param. The category
 * definitions (which game_type_ids belong to "Single Panna" and so on) live on the server, so
 * adding a category needs no change here.
 *
 * Server-side paging throughout: the collection holds ~345k bids, so the page never pulls more
 * than one screen of rows, and the summary is returned on page 1 only.
 */
export const BidCategoryReport = (props) => {
    const type = props.match.params.type;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [searchFromDate, setSearchFromDate] = useState('');
    const [searchToDate, setSearchToDate] = useState('');
    const [market, setMarket] = useState('');
    const [search, setSearch] = useState('');
    const [bidNo, setBidNo] = useState('');
    const [winStatus, setWinStatus] = useState('');
    const [isReset, setIsReset] = useState(false);

    const [marketList, setMarketList] = useState([]);
    const dfltSummary = { count: 0, total_amount: 0, total_win: 0, won: 0, breakdown: [] };
    const [summary, setSummary] = useState(dfltSummary);
    const [title, setTitle] = useState('Bid Report');
    const [description, setDescription] = useState('');

    const columns = useMemo(() => [
        { name: 'Date', selector: row => moment(row.report_date).format('DD-MM-YYYY'), width: '110px' },
        { name: 'Placed', selector: row => moment(row.createdAt).format('DD-MM-YYYY hh:mm A'), width: '165px' },
        { name: 'Mobile', selector: row => row.mobile_no || '-', width: '125px' },
        { name: 'Market', selector: row => row.market_name || '-', wrap: true },
        { name: 'Game', selector: row => row.game_label || '-', wrap: true },
        { name: 'Bid', selector: row => row.bid_value || '-', width: '95px' },
        { name: 'Bid No.', selector: row => row.tnx_id || '-', width: '120px' },
        { name: 'Amount', selector: row => money(row.amount), right: true, width: '110px' },
        { name: 'Status', selector: row => row.win_status || '-', width: '110px' },
        { name: 'Win', selector: row => (row.win_amount ? money(row.win_amount) : '-'), right: true, width: '110px' },
    ], []);

    function fetchReport(page, size = perPage) {
        setLoading(true);
        const sendd = { type, page, size, searchFromDate, searchToDate, market, search, bid_no: bidNo, winStatus };
        ReportsV2.bids(sendd).then(resp => {
            const r = resp.data;
            if (r.status == 1) {
                setData(r.data.docs);
                setTotalRows(r.total);
                setTitle(r.reportLabel || 'Bid Report');
                setDescription(r.reportDescription || '');
                if (page === 1) setSummary(r.summaryData || dfltSummary);
            } else {
                Alert.error(r.message);
                setData([]); setTotalRows(0); setSummary(dfltSummary);
            }
            setLoading(false);
        }).catch(err => { setLoading(false); Alert.error(err.message); });
    }

    function handlePageChange(page) { fetchReport(page); }
    function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); fetchReport(page, newPerPage); }

    const handleFilter = () => setIsReset(!isReset);
    const handleResetFilter = () => {
        setSearchFromDate(''); setSearchToDate(''); setMarket(''); setSearch(''); setBidNo(''); setWinStatus('');
        setIsReset(!isReset);
    };

    useEffect(() => {
        ReportsV2.markets().then(resp => {
            if (resp.data.status == 1) setMarketList(resp.data.data || []);
        }).catch(() => { /* market filter is optional */ });
    }, []);

    /* Re-fetch on filter apply AND on route change, so switching category in the menu reloads
       rather than leaving the previous category's rows on screen. */
    useEffect(() => { fetchReport(1); }, [isReset, type]);

    return (
        <section className="main-content marketadd_main_sec">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-1">{title}</h4>
                                    {description ? <p className="text-muted">{description}</p> : null}

                                    <div className="border border-primary p-3 mb-4">
                                        <div className="row">
                                            <div className="col-lg-2">
                                                <label className="form-label">From</label>
                                                <input type="date" className="form-control" max={getDateFormat()}
                                                    value={searchFromDate} onChange={e => setSearchFromDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-2">
                                                <label className="form-label">To</label>
                                                <input type="date" className="form-control" max={getDateFormat()} min={searchFromDate}
                                                    value={searchToDate} onChange={e => setSearchToDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Market</label>
                                                <select className="form-control" value={market} onChange={e => setMarket(e.target.value)}>
                                                    <option value="">All markets</option>
                                                    {marketList.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-lg-2">
                                                <label className="form-label">Status</label>
                                                <select className="form-control" value={winStatus} onChange={e => setWinStatus(e.target.value)}>
                                                    {WIN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Mobile no.</label>
                                                <input type="text" className="form-control" placeholder="e.g. 98765..."
                                                    value={search} onChange={e => setSearch(e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-lg-3">
                                                <label className="form-label">Bid no.</label>
                                                <input type="text" className="form-control" placeholder="Bid / transaction no."
                                                    value={bidNo} onChange={e => setBidNo(e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 d-flex align-items-end">
                                                <button className="btn btn-primary me-2" onClick={handleFilter}>Filter</button>
                                                <button className="btn btn-light" onClick={handleResetFilter}>Reset</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-responsive mb-4">
                                        <table className="table align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Game</th>
                                                    <th className="text-end">Bids</th>
                                                    <th className="text-end">Bid Amount</th>
                                                    <th className="text-end">Won</th>
                                                    <th className="text-end">Win Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summary.breakdown && summary.breakdown.length > 0
                                                    ? summary.breakdown.map((b, k) => (
                                                        <tr key={k}>
                                                            <td><span className="badge bg-success">{b.label}</span></td>
                                                            <td className="text-end">{b.count}</td>
                                                            <td className="text-end">{money(b.total_amount)}</td>
                                                            <td className="text-end">{b.won}</td>
                                                            <td className="text-end">{money(b.total_win)}</td>
                                                        </tr>
                                                    ))
                                                    : null}
                                            </tbody>
                                            <tfoot className="table-light">
                                                <tr>
                                                    <th>Total</th>
                                                    <th className="text-end">{summary.count}</th>
                                                    <th className="text-end">{money(summary.total_amount)}</th>
                                                    <th className="text-end">{summary.won}</th>
                                                    <th className="text-end">{money(summary.total_win)}</th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default withRouter(BidCategoryReport);
